import clsx from 'clsx'
import React, { FC, useRef } from 'react'
import styled, { css } from 'styled-components'

import { Heart } from '@Assets/SVG'
import HeartFilled from '@Assets/SVG/HeartFilled'
import { Product as ProductMParticle } from '@Events/ECommerce'
import { Product as ProductGTM } from '@Globals/Google/DataLayer'
import { up } from '@Globals/Style/breakpoints'
import { buttonReset } from '@Globals/Style/mixins'
import theme from '@Globals/Style/theme'
import { useTranslation } from '@Globals/Translations'

import { useWishList } from './useWishList'

export const WishlistButton = styled.button<{ selected: boolean, small?: string }>(({
  selected,
  small,
}) => css`
  position: relative;
  ${buttonReset};
  z-index: 1000;
  padding: 9px;

  ${up('desktop')} {
    min-width: 32px;
    min-height: 32px;
    padding: 7px;
  }

  &.small {
    border-radius: 50%;
    background-color: #ffffff;
    max-width: 40px;
    max-height: 40px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  &.default {
    width: 40px;
    height: 40px;

    svg {
      width: 22px;
      height: 20px;
    }

    ${up('desktop')} {
      svg {
        width: 26px;
        height: 26px;
      }
    }
  }

  ${selected && css`
    animation: zoom-in-zoom-out 200ms ease;
  `};

  @keyframes zoom-in-zoom-out {
    0% {
      transform: scale(${small === 'small' ? 0.65 : 1});
    }
    50% {
      transform: scale(${small === 'small' ? 1 : 1.5});
    }
    100% {
      transform: scale(${small === 'small' ? 1.5 : 1});
    }
  }
`)

const Box = styled.div<{ small: string | undefined }>`
  display: flex;
  align-items: center;
  justify-content: center;
`

interface AddToWishListProps {
  className?: string
  gtmProduct: ProductGTM
  id: string
  mParticleProduct: ProductMParticle
  size?: 'small'
}

export const AddToWishList: FC<AddToWishListProps> = ({ className, gtmProduct, id, mParticleProduct, size }) => {
  const { isSelected, wishListHandler } = useWishList(id, gtmProduct, mParticleProduct)
  const { t } = useTranslation('products')
  const showAnimation = useRef<boolean>(false)
  const isDefault = size !== 'small'
  const selected = isSelected(id)
  return (
    <WishlistButton
      aria-label={t(selected ? 'REMOVE_FROM_WISH_LIST' : 'ADD_TO_WISH_LIST')}
      className={clsx(className, {
        default: isDefault,
        small: !isDefault,
      })}
      onClick={(event) => {
        wishListHandler(event, showAnimation)
      }}
      selected={showAnimation.current}
      small={size}
    >
      <Box small={size}>
        {selected
          ? <HeartFilled fill={theme.colors.greyDark} />
          : <Heart fill={theme.colors.grey} />}
      </Box>
    </WishlistButton>
  )
}
